/**
 * deriveBadgeState — reine Funktion zur Ableitung des Lifecycle-Zustands
 * einer ScheduledPayment-Zeile für den `PaymentMethodBadge`.
 *
 * Phase 16 / BADGE-01 / BADGE-02 / D-22 / D-23 / D-24.
 *
 * Designprinzipien:
 * - Pure function, keine Side-Effects (Date.now() ist aus ES5 erlaubt).
 * - Keine Apollo-/React-Abhängigkeit — testbar ohne Rendering. (`badgeStateLabel`
 *   liest i18n, bleibt aber render-frei und damit auch für CSV/Export nutzbar.)
 * - Node-14-konform: keine `.at(-1)`, `.replaceAll`, `Object.hasOwn`,
 *   `structuredClone`, `Array.findLast`.
 * - Chargeback hat absoluten Vorrang vor Refund-States (Landmine #5).
 */

import i18n from "../../services/i18n";

export enum BadgeState {
    Paid = "paid",
    Open = "open",
    LinkSent = "link_sent",
    MethodChanged = "method_changed",
    Expired = "expired",
    Failed = "failed",
    Refunded = "refunded",
    PartiallyRefunded = "partially_refunded",
    Chargebacked = "chargebacked",
    /** Mollie-Zahlung/-Link aktiv storniert (`canceled`, `booking_cancelled`). */
    Canceled = "canceled",
    /** Zahlungslink aktiv entwertet (`link_voided`, `PATCH archived:true` bei Mollie). */
    LinkVoided = "link_voided",
}

/** BadgeState → i18n-Key unter `views.PaymentMethodBadge.lifecycleStates`. */
const BADGE_LABEL_KEY: {[key in BadgeState]: string} = {
    [BadgeState.Paid]: "paid",
    [BadgeState.Open]: "open",
    [BadgeState.LinkSent]: "linkSent",
    [BadgeState.MethodChanged]: "methodChanged",
    [BadgeState.Expired]: "expired",
    [BadgeState.Failed]: "failed",
    [BadgeState.Refunded]: "refunded",
    [BadgeState.PartiallyRefunded]: "partiallyRefunded",
    [BadgeState.Chargebacked]: "chargebacked",
    [BadgeState.Canceled]: "canceled",
    [BadgeState.LinkVoided]: "linkVoided",
};

/**
 * Beschriftung eines Lifecycle-States (de/en aus `lifecycleStates`).
 *
 * Bewusst eine FUNKTION, kein Modul-Level-Objekt: i18n-Blätter dürfen nicht zur
 * Modul-Ladezeit gebacken werden (sonst frieren sie die Sprache ein, die beim
 * Import aktiv war). Liegt hier statt in `PaymentMethodBadge.tsx`, damit auch
 * nicht-rendernde Aufrufer — z.B. der CSV-Export der Mollie-Zentrale — dieselben
 * Labels nutzen wie der Badge.
 */
export function badgeStateLabel(state: BadgeState): string {
    const labels = i18n.views.PaymentMethodBadge.lifecycleStates as Record<string, () => string>;
    return labels[BADGE_LABEL_KEY[state]]();
}

export type BadgeInput = {
    /**
     * Payment-Provider als String (kommt aus GraphQL als "Mollie" | "Manual" o.a.).
     * Wir vergleichen bewusst via `!== "Mollie"` → alles andere landet im
     * Manual-Pfad (safe default, verhindert Spoofing via unbekannte Werte).
     */
    paymentProvider: string;
    molliePaymentStatus?: string | null;
    paymentDone?: string | null;
    paymentAmount: number;
    /** Nur echte Mollie-Erstattungen; interne Guthaben stehen ausschliesslich im Gesamtmarker. */
    providerRefundedAmount?: number | null;
    /**
     * Tatsächlich bei Mollie gezahlter Betrag (FieldResolver `molliePaidAmount`).
     * Kann vom lokalen `paymentAmount` abweichen, wenn die Rate nach der Zahlung
     * bearbeitet wurde (Altdaten aus der Zeit vor dem Edit-Lock, Referenzfall
     * B-104: 1 € gezahlt, Rate später auf 2 € geändert). Mehr als diesen Betrag
     * kann Mollie nie erstatten — er ist deshalb die Bezugsgröße der Refund-States.
     *
     * `null`/`undefined` (Nicht-Mollie-Rate, kein Attempt) → Fallback auf
     * `paymentAmount` = unverändertes Verhalten.
     */
    molliePaidAmount?: number | null;
    methodChangedAt?: string | null;
    methodChangedByUserId?: string | null;
    linkExpiresAt?: string | null;
    latestMolliePaymentAttempt?: {
        chargedBackAt?: string | null;
        failedAt?: string | null;
        failureReason?: string | null;
        linkExpiresAt?: string | null;
        /**
         * BADGE-03: Mollie-Methode des AKTUELLEN Attempts. Wird gebraucht, um
         * eine banktransfer-Rate mit noch gültigem Link als „offen"/LinkSent
         * statt „Abgelaufen" zu behandeln (Parität zu `getDisplayMollieStatus`).
         */
        mollieMethod?: string | null;
    } | null;
};

/**
 * Bezugsgröße für die Refund-States und den Refund-Tooltip: der tatsächlich
 * gezahlte Betrag, sonst der Sollbetrag der Rate.
 *
 * Gleiche Konvention wie `getRefundCap` in `views/RefundPanel/RefundPanel.tsx`
 * (dort zusätzlich minus dem Gesamtmarker `refundedAmount`) — beide Stellen müssen dieselbe
 * Basis benutzen, sonst zeigt der Badge "Teilerstattet", während das
 * Refund-Panel keinen erstattbaren Restbetrag mehr anbietet.
 */
export function refundBaseAmount(sp: Pick<BadgeInput, "paymentAmount" | "molliePaidAmount">): number {
    return sp.molliePaidAmount != null ? Number(sp.molliePaidAmount) : Number(sp.paymentAmount);
}

/**
 * BADGE-03 — true, wenn `iso` ein gültiges Datum in der Zukunft ist.
 * Node-14-konform (kein `Date.parse`-Edgecase-Reliance), Date.now() ist ES5.
 */
function isFutureIso(iso?: string | null): boolean {
    if (!iso) return false;
    const t = new Date(iso).getTime();
    if (isNaN(t)) return false;
    return t > Date.now();
}

/**
 * Precedence (HÖCHSTE zuerst):
 *   1. chargedBackAt vorhanden            → Chargebacked  (Landmine #5: vor Refund)
 *   2. providerRefundedAmount >= refundBase    → Refunded
 *   3. 0 < providerRefundedAmount < refundBase → PartiallyRefunded
 *      (refundBase = molliePaidAmount ?? paymentAmount, s. `refundBaseAmount`)
 *   4. paymentDone vorhanden              → Paid
 *   5. Provider !== Mollie                → Open (keine Mollie-States)
 *   --- banktransfer-Normalisierung (BADGE-03): bei mollieMethod==="banktransfer"
 *       UND molliePaymentStatus==="expired" UND linkExpiresAt in Zukunft wird der
 *       Status auf "open" gedreht (analog Detail-Ansicht getDisplayMollieStatus /
 *       Server-Cache/DB-Regel normalizeBanktransferExpiryStatus). So fällt er unten
 *       in den linkExpiresAt-Zweig und wird zu LinkSent (statt Expired). ---
 *   6. status === "failed" | "creation_failed"        → Failed
 *   7. status === "expired"                           → Expired
 *   8. status === "canceled" | "booking_cancelled"    → Canceled
 *   9. status === "link_voided"                       → LinkVoided
 *  10. status === "method_changed" || methodChangedAt → MethodChanged
 *  11. linkExpiresAt in Zukunft                       → LinkSent (past → Expired)
 *   default                                           → Open
 *
 * Schritte 6–9 decken die terminalen Werte aus `MOLLIE_PAYMENT_STATUS`
 * (server/mollie/molliePaymentStatus.ts) ab und stehen bewusst VOR dem
 * linkExpiresAt-Zweig — sonst zeigt eine nicht mehr bezahlbare Rate "Link
 * gesendet" bzw. den Default "Offen". Verbleibend im Default: `open`, `pending`
 * und `superseded` (dort läuft ein neuer Versuch → "offen" ist korrekt).
 */
export function deriveBadgeState(sp: BadgeInput): BadgeState {
    const attempt = sp.latestMolliePaymentAttempt;
    if (attempt && attempt.chargedBackAt) return BadgeState.Chargebacked;

    const providerRefundedAmount = Number(sp.providerRefundedAmount || 0);
    if (providerRefundedAmount > 0) {
        if (providerRefundedAmount >= refundBaseAmount(sp)) return BadgeState.Refunded;
        return BadgeState.PartiallyRefunded;
    }

    if (sp.paymentDone) return BadgeState.Paid;
    if (sp.paymentProvider !== "Mollie") return BadgeState.Open;

    // linkExpiresAt aus SP-Cache oder (autoritativ) aus dem aktuellen Attempt.
    const expiresAtIso = sp.linkExpiresAt || (attempt && attempt.linkExpiresAt) || null;

    // BADGE-03: banktransfer meldet bei Mollie früh "expired", obwohl der
    // Zahlungslink laut linkExpiresAt noch gültig ist. Wir drehen den Status
    // dann auf "open" — der Posten ist weiterhin zahlbar und soll als
    // "Link gesendet" erscheinen, nicht als "Abgelaufen". Deckungsgleich mit
    // getDisplayMollieStatus (Detail-Ansicht) und der Server-Cache/DB-Regel
    // normalizeBanktransferExpiryStatus(isLiveMollieStatus=false).
    const mollieMethod = (attempt && attempt.mollieMethod) || null;
    const rawStatus = sp.molliePaymentStatus || "";
    const status =
        mollieMethod === "banktransfer" && rawStatus === "expired" && isFutureIso(expiresAtIso)
            ? "open"
            : rawStatus;

    if (status === "failed" || status === "creation_failed") return BadgeState.Failed;
    if (status === "expired") return BadgeState.Expired;
    // Terminale Status VOR dem linkExpiresAt-Zweig: eine stornierte/entwertete Rate mit
    // noch in der Zukunft liegendem linkExpiresAt fiel sonst auf "Link gesendet" (blau,
    // suggeriert zahlbar) bzw. ohne Attempt auf den Default "Offen" durch — obwohl der
    // Link bei Mollie archiviert und nicht mehr bezahlbar ist.
    if (status === "canceled" || status === "booking_cancelled") return BadgeState.Canceled;
    if (status === "link_voided") return BadgeState.LinkVoided;
    if (status === "method_changed" || sp.methodChangedAt) return BadgeState.MethodChanged;

    if (expiresAtIso) {
        const expiresAt = new Date(expiresAtIso).getTime();
        if (expiresAt > Date.now()) return BadgeState.LinkSent;
        return BadgeState.Expired;
    }

    return BadgeState.Open;
}
